import type { ComponentProps, ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { nanoid } from 'nanoid/non-secure';

import type { NoticeBanner } from '../NoticeBanner';

//
// ─── CONTEXT ────────────────────────────────────────────────────────────────────
//

const NoticeBannersStackContext = createContext<NoticeBannersStackContextValue | undefined>(
  undefined,
);

/**
 * The actions, split out from the stack so components that only dispatch
 * banners are not re-rendered every time the stack changes. Reading the full
 * context subscribes to `stack` too, which turns one banner show/hide into a
 * re-render of every dispatcher in the tree.
 */
const NoticeBannersStackActionsContext = createContext<
  NoticeBannersStackActionsContextValue | undefined
>(undefined);

export const NoticeBannersStackProvider = (props: NoticeBannersStackProviderProps) => {
  const { children, initialBanners = [] } = props;

  const [isEnabled, setIsEnabled] = useState(true);
  const [stack, setStack] = useState<NoticeBannersStackContextValue['stack']>(initialBanners);

  // ────────────────────────────────────────────────────────────────────

  const push = useCallback<NoticeBannersStackContextValue['push']>(
    (banner, preventDuplicates) => {
      if (!isEnabled) return;

      setStack((currentStack) => {
        const bannerAlreadyExists =
          preventDuplicates && checkIfBannerAlreadyExists(currentStack, banner.id);

        if (bannerAlreadyExists) return currentStack;

        return [{ id: nanoid(), ...banner }, ...currentStack];
      });
    },
    [isEnabled],
  );

  const pop = useCallback<NoticeBannersStackContextValue['pop']>(() => {
    setStack((currentStack) => currentStack.slice(1));
  }, []);

  const clear = useCallback<NoticeBannersStackContextValue['clear']>(() => {
    setStack([]);
  }, []);

  const remove = useCallback<NoticeBannersStackContextValue['remove']>((id: string) => {
    setStack((currentStack) => currentStack.filter((banner) => banner.id !== id));
  }, []);

  const enable = useCallback<NoticeBannersStackContextValue['enable']>(() => {
    setIsEnabled(true);
  }, []);

  const disable = useCallback<NoticeBannersStackContextValue['disable']>(() => {
    setIsEnabled(false);
  }, []);

  // ────────────────────────────────────────────────────────────────────

  const actionsValue = useMemo(
    () => ({ push, pop, remove, clear, enable, disable }),
    [push, pop, remove, clear, enable, disable],
  );

  const providerValue = useMemo(() => ({ stack, ...actionsValue }), [stack, actionsValue]);

  return (
    <NoticeBannersStackActionsContext.Provider value={actionsValue}>
      <NoticeBannersStackContext.Provider value={providerValue}>
        {children}
      </NoticeBannersStackContext.Provider>
    </NoticeBannersStackActionsContext.Provider>
  );
};

// ─── HOOKS ──────────────────────────────────────────────────────────────────────

export const useNoticeBannersStackContext = () => {
  const context = useContext(NoticeBannersStackContext);

  if (context === undefined) {
    throw new Error(
      'useNoticeBannersStackContext must be used within a <NoticeBannersStackProvider>',
    );
  }

  return context;
};

/**
 * Banner actions without a subscription to the stack. Prefer this over
 * {@link useNoticeBannersStackContext} anywhere the stack itself is not
 * rendered, and especially in list items, where the shared subscription
 * multiplies across every row.
 */
export const useNoticeBannersStackActions = () => {
  const context = useContext(NoticeBannersStackActionsContext);

  if (context === undefined) {
    throw new Error(
      'useNoticeBannersStackActions must be used within a <NoticeBannersStackProvider>',
    );
  }

  return context;
};

// ─── UTILS ──────────────────────────────────────────────────────────────────────

const checkIfBannerAlreadyExists = (currentStack: NoticeBannersStackItems, bannerId = '') => {
  return currentStack.some((stackBanner) => stackBanner.id === bannerId);
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type NoticeBannersStackProviderProps = Readonly<{
  children: ReactNode;
  initialBanners?: NoticeBannersStackItems;
}>;

type NoticeBannersStackContextValue = Readonly<{
  stack: NoticeBannersStackItems;
}> &
  NoticeBannersStackActionsContextValue;

type NoticeBannersStackActionsContextValue = Readonly<{
  push: (banner: NoticeBannerStackItem, preventDuplicates?: boolean) => void;
  pop: () => void;
  remove: (id: string) => void;
  clear: () => void;
  disable: () => void;
  enable: () => void;
}>;

type NoticeBannerStackItem = Readonly<{
  targetId?: string;
  id?: string;
}> &
  NoticeBannersStackItemProps;

type NoticeBannersStackItemProps = ComponentProps<typeof NoticeBanner>;

export type NoticeBannersStackItems = readonly NoticeBannerStackItem[];
